import React from 'react'
import PropTypes from 'prop-types'
import { formatValueByColumn } from '../helpers/chart-formatters'
import { KpiValueColumnOption, KpiAggregationOption, KpiAnalyticOption } from './chart-options'

function KpiChart({ value, column, aggregation }) {
  // Determine if the value should be displayed as currency or regular number
  // Use compact format (K, M, B, T) for large numbers exceeding 6 characters
  const displayValue = typeof value === 'number' ? formatValueByColumn(value, column, aggregation, true) : value
  const actualValue = typeof value === 'number' ? formatValueByColumn(value, column, aggregation, false) : value

  // Show tooltip with actual value if it differs from display value
  const showTooltip = displayValue !== actualValue

  return (
    <div className="d-flex flex-column justify-content-center align-items-center text-center py-4 h-100">
      <div
        className="display-3 fw-bold text-primary"
        style={{ lineHeight: '1', fontSize: 'clamp(2rem, 8vw, 4rem)' }}
        title={showTooltip ? actualValue : undefined}
      >
        {displayValue}
      </div>
    </div>
  )
}

KpiChart.propTypes = {
  // Nullable: a min/max over a date column with no rows has no zero to fall back on, so the API
  // sends null and the card renders blank rather than a misleading "0"
  // (Charts::BaseChart#empty_extremum_for).
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  column: PropTypes.object,
  aggregation: PropTypes.string
}

// Chart configuration for KPI charts
export const kpiChartConfig = {
  requiredFields: ['title', 'kpiValueColumn'],

  validate: (formData) => {
    const errors = {}
    if (!formData.kpiValueColumn) {
      errors.kpiValueColumn = 'Value column is required for KPI charts'
    }
    return errors
  },

  isQueryEnabled: (formData, hasChartId) => {
    return !!(hasChartId && formData.kpiValueColumn)
  },

  buildChartParams: (formData) => {
    const params = {
      value_column_id: formData.kpiValueColumn,
      aggregation_method: formData.kpiAggregation
    }

    // Handle analytics filter: either has_no_analytics or analytic_id, but not both
    if (formData.kpiAnalytic === 'none') {
      params.has_no_analytics = true
      params.analytic_id = null
    } else if (formData.kpiAnalytic) {
      params.has_no_analytics = false
      params.analytic_id = formData.kpiAnalytic
    } else {
      // No analytics filter selected (show all)
      params.has_no_analytics = false
      params.analytic_id = null
    }

    return params
  },

  // Build query params for API request
  buildQueryParams: (baseParams, formData, columns) => {
    const params = { ...baseParams }
    if (formData.kpiValueColumn) {
      const kpiCol = columns?.find((c) => c.id === formData.kpiValueColumn)
      params.kpi_column = kpiCol?.database_column_name
      params.kpi_aggregation = formData.kpiAggregation || 'sum'
    }
    if (formData.kpiAnalytic && formData.kpiAnalytic !== 'none') {
      params.kpi_has_no_analytics = false
      params.kpi_analytic = formData.kpiAnalytic
    } else if (formData.kpiAnalytic && formData.kpiAnalytic === 'none') {
      params.kpi_has_no_analytics = true
      params.kpi_analytic = null
    } else {
      params.kpi_has_no_analytics = false
      params.kpi_analytic = null
    }
    return params
  },

  // Render chart-specific options in the modal form
  renderOptions: ({
    Form,
    formState,
    setters,
    validationErrors,
    hasSubmitted,
    getValueColumns,
    getAggregationOptions,
    getColumnDisplayName,
    getAggregationLabel,
    municipality,
    analytics,
    sectionId
  }) => {
    const { kpiValueColumn, kpiAggregation, kpiAnalytic } = formState
    const { setKpiValueColumn, setKpiAggregation, setKpiAnalytic, setValidationErrors } = setters

    return (
      <>
        <KpiValueColumnOption
          Form={Form}
          kpiValueColumn={kpiValueColumn}
          setKpiValueColumn={setKpiValueColumn}
          kpiAggregation={kpiAggregation}
          setKpiAggregation={setKpiAggregation}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getValueColumns={getValueColumns}
          getAggregationOptions={getAggregationOptions}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
        />

        <KpiAggregationOption
          Form={Form}
          kpiAggregation={kpiAggregation}
          setKpiAggregation={setKpiAggregation}
          kpiValueColumn={kpiValueColumn}
          getAggregationOptions={getAggregationOptions}
          getAggregationLabel={getAggregationLabel}
        />

        <KpiAnalyticOption
          Form={Form}
          kpiAnalytic={kpiAnalytic}
          setKpiAnalytic={setKpiAnalytic}
          analytics={analytics}
          sectionId={sectionId}
        />
      </>
    )
  }
}

export default React.memo(KpiChart)